/**
 * User profile types — signup-and-billing Phase 1.
 *
 * `UserProfilePatch` — the 6-question signup wizard collects firm/role/
 * asset_classes/markets/focus/volume; Settings → Account lets the user edit
 * any of them later. Both surfaces share the same `PATCH /api/users/me`
 * endpoint, so every field is optional + the server enforces "all 6 must be
 * present at the moment onboarding_completed flips to true" in the service
 * layer (not here).
 *
 * `UserProfile` — the wire shape returned by `GET /api/admin/me/status`.
 * Mirrors the canonical DB shape in `@arcanum-hq/supabase/queries/user-profiles.ts`
 * but lives here so `apps/web` can import it without violating the web's
 * "no @arcanum-hq/supabase imports" architectural invariant. The two
 * declarations are intentional duplicates — the supabase one is the
 * server-side query return type; this one is the API wire shape that web
 * consumes via `useUserProfile` / `useApprovalStatus`. Drift between them
 * is caught at the API boundary (the controller's response is typed against
 * the supabase shape; the wire JSON gets re-validated against this Zod schema
 * client-side).
 */
import { z } from 'zod';
const FIELD_MIN_LENGTH = 2;
const FIELD_MAX_LENGTH = 1000;
export const userProfilePatchSchema = z
    .object({
    firm_name: z
        .string()
        .trim()
        .min(FIELD_MIN_LENGTH)
        .max(FIELD_MAX_LENGTH)
        .optional(),
    // `work_role` — the user's self-reported job title (e.g. "Operator",
    // "Developer"). Renamed from `role` 2026-05-25 to remove the field-name
    // collision with `auth.users.app_metadata.role` (the admin claim). User
    // input — NEVER read for platform-privilege decisions.
    work_role: z
        .string()
        .trim()
        .min(FIELD_MIN_LENGTH)
        .max(FIELD_MAX_LENGTH)
        .optional(),
    asset_classes: z
        .string()
        .trim()
        .min(FIELD_MIN_LENGTH)
        .max(FIELD_MAX_LENGTH)
        .optional(),
    markets: z
        .string()
        .trim()
        .min(FIELD_MIN_LENGTH)
        .max(FIELD_MAX_LENGTH)
        .optional(),
    focus: z
        .string()
        .trim()
        .min(FIELD_MIN_LENGTH)
        .max(FIELD_MAX_LENGTH)
        .optional(),
    volume: z
        .string()
        .trim()
        .min(FIELD_MIN_LENGTH)
        .max(FIELD_MAX_LENGTH)
        .optional(),
    onboarding_completed: z.boolean().optional(),
    terms_accepted_at: z.string().datetime().optional(),
})
    .strict()
    .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided',
});
/**
 * Wire shape of the `user_profiles` row returned by GET /api/admin/me/status.
 * Mirrors the DB columns one-for-one. See module header for why this is
 * declared here AND in the supabase package.
 */
export const userProfileSchema = z.object({
    user_id: z.string().uuid(),
    email: z.string().nullable(),
    status: z.enum(['pending', 'approved', 'rejected']),
    // `work_role` — user-reported job title (renamed from `role` 2026-05-25).
    // UNTRUSTED — never gate privilege on this. Platform admin status lives
    // in `admin_role` (the JWT-derived projection below).
    work_role: z.string().nullable(),
    // `admin_role` — derived from `auth.users.app_metadata.role` server-side
    // by the `listUsers` mapper. The ONLY field on this DTO that reflects
    // platform privilege; populated only by `promoteToAdmin`/`demoteFromAdmin`.
    admin_role: z.string().nullable(),
    arc_id: z.string().nullable(),
    approved_by: z.string().nullable(),
    approved_at: z.string().nullable(),
    rejected_reason: z.string().nullable(),
    invited_by: z.string().nullable(),
    invited_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    // signup-and-billing Phase 1 columns
    firm_name: z.string().nullable(),
    asset_classes: z.string().nullable(),
    markets: z.string().nullable(),
    focus: z.string().nullable(),
    volume: z.string().nullable(),
    onboarding_completed: z.boolean(),
    founding_partner: z.boolean(),
    terms_accepted_at: z.string().nullable(),
    // in-app-checkout Phase 1 column (ADR 0006): timestamp set server-side when
    // user submits a Bespoke-tier inquiry. NULL = not yet requested. Additive +
    // nullable — pre-feature rows + non-requesters remain NULL. The value already
    // rides the existing select('*') read-path; NO migration needed here.
    bespoke_requested_at: z.string().nullable(),
});
